import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Settings, 
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Save,
  Bell,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AdminSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('agency');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [agencyRes, smtpRes] = await Promise.all([
        api.get('/settings/agency-info').catch(() => ({ data: null })),
        api.get('/settings/smtp').catch(() => ({ data: null }))
      ]);
      
      setSettings(agencyRes.data || {
        name: 'MSC L\'AGENCE',
        email: 'contact@msc.fr',
        phone: '',
        address: '',
        postal_code: '',
        city: '',
        country: 'France',
        website: '',
        siret: '',
        tva_number: '',
      });
      
      setSmtpSettings(smtpRes.data || {
        host: '',
        port: 587,
        username: '',
        password: '',
        sender_email: '',
        sender_name: '',
        use_tls: true
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAgency = async () => {
    setSaving(true);
    try {
      await api.put('/settings/agency-info', settings);
      toast.success('Informations sauvegardées');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      await api.put('/settings/smtp', smtpSettings);
      toast.success('Configuration SMTP sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    try {
      await api.post('/settings/smtp/test');
      toast.success('Email de test envoyé !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du test');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Paramètres</h1>
          <p className="text-[#64748b]">Configuration générale de votre agence</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="agency"><Building2 className="w-4 h-4 mr-2" />Agence</TabsTrigger>
          <TabsTrigger value="email"><Mail className="w-4 h-4 mr-2" />Email (SMTP)</TabsTrigger>
        </TabsList>

        <TabsContent value="agency" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations de l'agence</CardTitle>
              <CardDescription>Ces informations apparaissent sur votre site vitrine et vos documents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l'agence</Label>
                  <Input 
                    value={settings?.name || ''}
                    onChange={(e) => setSettings({...settings, name: e.target.value})}
                    placeholder="MSC L'AGENCE"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Site web</Label>
                  <Input 
                    value={settings?.website || ''}
                    onChange={(e) => setSettings({...settings, website: e.target.value})}
                    placeholder="https://msc.fr"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email de contact</Label>
                  <Input 
                    type="email"
                    value={settings?.email || ''}
                    onChange={(e) => setSettings({...settings, email: e.target.value})}
                    placeholder="contact@msc.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input 
                    value={settings?.phone || ''}
                    onChange={(e) => setSettings({...settings, phone: e.target.value})}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Adresse</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input 
                  value={settings?.address || ''}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                  placeholder="123 Rue Exemple"
                />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input 
                    value={settings?.postal_code || ''}
                    onChange={(e) => setSettings({...settings, postal_code: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input 
                    value={settings?.city || ''}
                    onChange={(e) => setSettings({...settings, city: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input 
                    value={settings?.country || ''}
                    onChange={(e) => setSettings({...settings, country: e.target.value})}
                    placeholder="France"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Informations légales</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input 
                  value={settings?.siret || ''}
                  onChange={(e) => setSettings({...settings, siret: e.target.value})}
                  placeholder="123 456 789 00012"
                />
              </div>
              <div className="space-y-2">
                <Label>N° TVA Intracommunautaire</Label>
                <Input 
                  value={settings?.tva_number || ''}
                  onChange={(e) => setSettings({...settings, tva_number: e.target.value})}
                  placeholder="FR12345678901"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveAgency} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="email" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Configuration SMTP</CardTitle>
              <CardDescription>Paramètres pour l'envoi d'emails (notifications, factures, etc.)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serveur SMTP</Label>
                  <Input 
                    value={smtpSettings?.host || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, host: e.target.value})}
                    placeholder="smtp.exemple.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input 
                    type="number"
                    value={smtpSettings?.port || 587}
                    onChange={(e) => setSmtpSettings({...smtpSettings, port: parseInt(e.target.value) || 587})}
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Utilisateur</Label>
                  <Input 
                    value={smtpSettings?.username || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, username: e.target.value})}
                    placeholder="user@exemple.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <Input 
                    type="password"
                    value={smtpSettings?.password || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, password: e.target.value})}
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email expéditeur</Label>
                  <Input 
                    type="email"
                    value={smtpSettings?.sender_email || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, sender_email: e.target.value})}
                    placeholder="noreply@msc.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom expéditeur</Label>
                  <Input 
                    value={smtpSettings?.sender_name || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, sender_name: e.target.value})}
                    placeholder="MSC L'AGENCE"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Switch 
                  checked={smtpSettings?.use_tls || false}
                  onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, use_tls: checked})}
                />
                <Label>Utiliser TLS/SSL</Label>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleTestSmtp}>
              <Mail className="w-4 h-4 mr-2" />
              Envoyer un test
            </Button>
            <Button onClick={handleSaveSmtp} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettingsPage;
